import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  css,
  cssManager,
  DeesElement,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { zIndexRegistry } from '../../00zindex.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesWindowLayer } from '../../00group-overlay/dees-windowlayer/dees-windowlayer.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import type { IDateEvent } from './types.js';
import { createCalendarDate, dateValidationMessage } from './date-validation.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-datepicker-popup': DeesInputDatepickerPopup;
  }
}

@customElement('dees-input-datepicker-popup')
export class DeesInputDatepickerPopup extends DeesElement {
  // Properties set by the parent
  @property({ attribute: false })
  accessor triggerRect: DOMRect | null = null;

  @property({ attribute: false })
  accessor ownerComponent: HTMLElement | null = null;

  @property({ type: Boolean })
  accessor enableTime: boolean = false;

  @property({ type: String })
  accessor timeFormat: '24h' | '12h' = '24h';

  @property({ type: Number })
  accessor minuteIncrement: number = 1;

  @property({ type: Number })
  accessor weekStartsOn: 0 | 1 = 1;

  @property({ type: String })
  accessor minDate: string = '';

  @property({ type: String })
  accessor maxDate: string = '';

  @property({ type: Array })
  accessor disabledDates: string[] = [];

  @property({ type: Boolean })
  accessor enableTimezone: boolean = false;

  @property({ type: String })
  accessor timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  @property({ type: Array })
  accessor events: IDateEvent[] = [];

  @property({ type: Boolean })
  accessor opensToTop: boolean = false;

  // Internal state
  @state()
  accessor selectedDate: Date | null = null;

  @state()
  accessor viewDate: Date = new Date();

  @state()
  accessor selectedHour: number = 0;

  @state()
  accessor selectedMinute: number = 0;

  @state()
  accessor menuZIndex: number = 1000;

  @state()
  accessor visible: boolean = false;

  private windowLayer: DeesWindowLayer | null = null;
  private isDestroying: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        pointer-events: none;
      }

      * {
        box-sizing: border-box;
      }

      .calendar-popup {
        position: fixed;
        pointer-events: auto;
        will-change: transform, opacity;
        transition:
          opacity var(--dees-transition-fast) var(--dees-ease-standard),
          transform var(--dees-transition-fast) var(--dees-ease-standard);
        opacity: 0;
        transform: translateY(-4px);
        background: var(--dees-material-regular-bg-opaque);
        border: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-lg);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        padding: 12px;
        user-select: none;
        width: min(290px, calc(100vw - 24px));
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .calendar-popup {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        .calendar-popup {
          background: var(--dees-material-regular-bg-opaque);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      .calendar-popup.top {
        transform: translateY(4px);
      }

      .calendar-popup.show {
        transform: translateY(0);
        opacity: 1;
      }

      /* Calendar Header */
      .calendar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        gap: 8px;
      }

      .month-year-display {
        font-weight: 500;
        font-size: 14px;
        color: var(--dees-color-text-primary);
        flex: 1;
        text-align: center;
      }

      .nav-button {
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: var(--dees-radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--dees-color-text-secondary);
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .nav-button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      /* Weekday headers */
      .weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        margin-bottom: 4px;
      }

      .weekday {
        text-align: center;
        font-size: 12px;
        font-weight: 400;
        color: var(--dees-color-text-muted);
        padding: 0 0 8px 0;
      }

      /* Days grid */
      .days-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
      }

      .day {
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: var(--dees-radius-full);
        font-size: var(--dees-font-control-size);
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-primary);
        border: none;
        width: 36px;
        height: 36px;
        background: transparent;
        position: relative;
      }

      .day:hover:not(.disabled) {
        background: var(--dees-color-hover);
      }

      .day.other-month {
        color: var(--dees-color-text-muted);
      }

      .day.today {
        color: var(--dees-color-accent-primary);
        font-weight: 600;
      }

      .day.selected {
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        font-weight: 500;
      }

      .day.disabled {
        color: var(--dees-color-label-quaternary);
        cursor: not-allowed;
      }

      /* Event indicators */
      .event-indicator {
        position: absolute;
        bottom: 4px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 2px;
      }

      .event-dot {
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: var(--dees-color-text-muted);
      }

      .event-dot.info { background: var(--dees-color-accent-primary); }
      .event-dot.warning { background: var(--dees-color-text-warning); }
      .event-dot.success { background: var(--dees-color-text-success); }
      .event-dot.error { background: var(--dees-color-text-error); }

      .event-count {
        position: absolute;
        top: 2px;
        right: 2px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        background: var(--dees-color-accent-error);
        color: var(--dees-color-on-accent);
        border-radius: var(--dees-radius-full);
        font-size: 10px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .event-tooltip {
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: var(--dees-color-tooltip-bg);
        color: var(--dees-color-tooltip-fg);
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 10;
      }

      .day.has-event:hover .event-tooltip { opacity: 1; }

      /* Time selector */
      .time-selector {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--dees-color-border-default);
      }

      .time-selector-title {
        font-size: 12px;
        font-weight: 500;
        margin-bottom: 8px;
        color: var(--dees-color-text-secondary);
      }

      .time-inputs {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .time-input {
        width: 65px;
        height: var(--dees-control-height-md);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        padding: 0 12px;
        font-size: var(--dees-font-control-size);
        text-align: center;
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        transition:
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .time-input:focus {
        outline: none;
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .time-separator {
        font-size: 14px;
        font-weight: 500;
        color: var(--dees-color-text-muted);
      }

      .am-pm-selector { display: flex; gap: 4px; margin-left: 8px; }

      .am-pm-button {
        padding: 6px 12px;
        border: 1px solid var(--dees-color-border-default);
        background: var(--dees-color-bg-primary);
        border-radius: var(--dees-radius-md);
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-secondary);
      }

      .am-pm-button.selected {
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        border-color: var(--dees-color-accent-primary);
      }

      .am-pm-button:hover:not(.selected) {
        background: var(--dees-color-hover);
      }

      /* Timezone selector */
      .timezone-selector {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--dees-color-border-default);
      }

      .timezone-selector-title {
        font-size: 12px;
        font-weight: 500;
        margin-bottom: 8px;
        color: var(--dees-color-text-secondary);
      }

      .timezone-select {
        width: 100%;
        height: var(--dees-control-height-md);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        padding: 0 12px;
        font-size: var(--dees-font-control-size);
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        cursor: pointer;
      }

      /* Action buttons */
      .calendar-actions {
        display: flex;
        gap: 8px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--dees-color-border-default);
      }

      .action-button {
        flex: 1;
        height: var(--dees-control-height-md);
        border: none;
        border-radius: var(--dees-radius-md);
        font-size: var(--dees-font-control-size);
        font-weight: 500;
        cursor: pointer;
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .today-button {
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        color: var(--dees-color-text-primary);
      }

      .today-button:hover {
        background: var(--dees-color-hover);
      }

      .clear-action-button {
        background: transparent;
        border: 1px solid transparent;
        color: var(--dees-color-text-secondary);
      }

      .clear-action-button:hover {
        background: color-mix(in srgb, var(--dees-color-accent-error) 10%, transparent);
        color: var(--dees-color-accent-error);
      }
      @media (pointer: coarse) {
        .calendar-popup { width: min(348px, calc(100vw - 24px)); }
        .day { width: 100%; height: 44px; }
        .nav-button { width: 44px; height: 44px; }
        .time-input, .timezone-select, .action-button, .am-pm-button { min-height: 44px; }
      }
    `,
  ];

  private static readonly MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  private static readonly TIMEZONES = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Europe/Berlin', label: 'Berlin' },
    { value: 'Europe/Moscow', label: 'Moscow' },
    { value: 'Asia/Dubai', label: 'Dubai' },
    { value: 'Asia/Kolkata', label: 'India Standard Time' },
    { value: 'Asia/Shanghai', label: 'China Standard Time' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Sydney', label: 'Sydney' },
    { value: 'Pacific/Auckland', label: 'Auckland' },
  ];

  public render(): TemplateResult {
    if (!this.triggerRect) return html``;

    const posStyle = this.computePositionStyle();
    const weekDays = this.weekStartsOn === 1
      ? ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
      : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const days = this.getDaysInMonth();
    const isAM = this.selectedHour < 12;

    return html`
      <div
        class="calendar-popup ${this.visible ? 'show' : ''} ${this.opensToTop ? 'top' : 'bottom'}"
        role="dialog" aria-label="Choose a date"
        @keydown=${(event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.dispatchEvent(new CustomEvent('close-request'));
            this.ownerComponent?.shadowRoot?.querySelector('input')?.focus();
          }
        }}
        style="${posStyle}; z-index: ${this.menuZIndex};"
      >
        <div class="calendar-header">
          <button type="button" class="nav-button" aria-label="Previous month" @click=${this.previousMonth}>
            <dees-icon icon="lucide:chevronLeft" iconSize="16"></dees-icon>
          </button>
          <div class="month-year-display">
            ${DeesInputDatepickerPopup.MONTH_NAMES[this.viewDate.getMonth()]} ${this.viewDate.getFullYear()}
          </div>
          <button type="button" class="nav-button" aria-label="Next month" @click=${this.nextMonth}>
            <dees-icon icon="lucide:chevronRight" iconSize="16"></dees-icon>
          </button>
        </div>

        <div class="weekdays">
          ${weekDays.map(day => html`<div class="weekday">${day}</div>`)}
        </div>

        <div class="days-grid">
          ${days.map(day => {
            const isToday = this.isToday(day);
            const isSelected = this.isSelected(day);
            const isOtherMonth = day.getMonth() !== this.viewDate.getMonth();
            const isDayDisabled = this.isDayDisabled(day);
            const dayEvents = this.getEventsForDate(day);
            const hasEvents = dayEvents.length > 0;
            const totalEventCount = dayEvents.reduce((sum, event) => sum + (event.count || 1), 0);

            return html`
              <button type="button"
                class="day ${isOtherMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isDayDisabled ? 'disabled' : ''} ${hasEvents ? 'has-event' : ''}"
                ?disabled=${isDayDisabled}
                aria-label=${`${String(day.getFullYear()).padStart(4, '0')}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`}
                aria-pressed=${isSelected ? 'true' : 'false'}
                @click=${() => !isDayDisabled && this.handleSelectDate(day)}
              >
                ${day.getDate()}
                ${hasEvents ? html`
                  ${totalEventCount > 3 ? html`
                    <span class="event-count">${totalEventCount}</span>
                  ` : html`
                    <span class="event-indicator">
                      ${dayEvents.slice(0, 3).map(event => html`
                        <span class="event-dot ${event.type || 'info'}"></span>
                      `)}
                    </span>
                  `}
                  ${dayEvents[0].title ? html`
                    <span class="event-tooltip">
                      ${dayEvents[0].title}
                      ${totalEventCount > 1 ? html` (+${totalEventCount - 1} more)` : ''}
                    </span>
                  ` : ''}
                ` : ''}
              </button>
            `;
          })}
        </div>

        ${this.enableTime ? html`
          <div class="time-selector">
            <div class="time-selector-title">Time</div>
            <div class="time-inputs">
              <input type="number" class="time-input"
                .value=${this.timeFormat === '12h'
                  ? (this.selectedHour === 0 ? 12 : this.selectedHour > 12 ? this.selectedHour - 12 : this.selectedHour).toString().padStart(2, '0')
                  : this.selectedHour.toString().padStart(2, '0')}
                @input=${this.handleHourInput}
                min="${this.timeFormat === '12h' ? 1 : 0}"
                max="${this.timeFormat === '12h' ? 12 : 23}"
              />
              <span class="time-separator">:</span>
              <input type="number" class="time-input"
                .value=${this.selectedMinute.toString().padStart(2, '0')}
                @input=${this.handleMinuteInput}
                min="0" max="59" step="${this.minuteIncrement || 1}"
              />
              ${this.timeFormat === '12h' ? html`
                <div class="am-pm-selector">
                  <button class="am-pm-button ${isAM ? 'selected' : ''}" @click=${() => this.setAMPM('am')}>AM</button>
                  <button class="am-pm-button ${!isAM ? 'selected' : ''}" @click=${() => this.setAMPM('pm')}>PM</button>
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}

        ${this.enableTimezone ? html`
          <div class="timezone-selector">
            <div class="timezone-selector-title">Timezone</div>
            <select class="timezone-select" .value=${this.timezone} @change=${this.handleTimezoneChange}>
              ${DeesInputDatepickerPopup.TIMEZONES.map(tz => html`
                <option value="${tz.value}" ?selected=${tz.value === this.timezone}>${tz.label}</option>
              `)}
            </select>
          </div>
        ` : ''}

        <div class="calendar-actions">
          <button type="button" class="action-button today-button" ?disabled=${this.isDayDisabled(new Date())} @click=${this.handleSelectToday}>Today</button>
          <button type="button" class="action-button clear-action-button" @click=${this.handleClear}>Clear</button>
        </div>
      </div>
    `;
  }

  private computePositionStyle(): string {
    const rect = this.triggerRect!;
    const width = Math.min(window.matchMedia('(pointer: coarse)').matches ? 348 : 290, window.innerWidth - 24);
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));

    if (this.opensToTop) {
      const bottom = window.innerHeight - rect.top + 4;
      return `left: ${left}px; bottom: ${bottom}px; top: auto; max-height:${Math.max(0, rect.top - 16)}px`;
    } else {
      const top = rect.bottom + 4;
      return `left: ${left}px; top: ${top}px; max-height:${Math.max(0, window.innerHeight - top - 12)}px`;
    }
  }

  // Calendar logic
  private getDaysInMonth(): Date[] {
    const firstDay = new Date(this.viewDate);
    firstDay.setDate(1);
    firstDay.setHours(12, 0, 0, 0);

    const startOffset = this.weekStartsOn === 1
      ? (firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1)
      : firstDay.getDay();

    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(firstDay);
      day.setDate(1 - startOffset + index);
      return day;
    });
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  }

  private isSelected(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.getDate() === this.selectedDate.getDate() && date.getMonth() === this.selectedDate.getMonth() && date.getFullYear() === this.selectedDate.getFullYear();
  }

  private isDayDisabled(date: Date): boolean {
    return Boolean(dateValidationMessage(date, this));
  }

  private getEventsForDate(date: Date): IDateEvent[] {
    if (!this.events?.length) return [];
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return this.events.filter(e => e.date === dateStr);
  }

  private previousMonth(): void {
    const date = new Date(this.viewDate);
    date.setMonth(date.getMonth() - 1, 1);
    this.viewDate = date;
  }

  private nextMonth(): void {
    const date = new Date(this.viewDate);
    date.setMonth(date.getMonth() + 1, 1);
    this.viewDate = date;
  }

  // Event dispatching
  private handleSelectDate(day: Date): void {
    if (this.isDayDisabled(day)) return;
    const selected = createCalendarDate(day.getFullYear(), day.getMonth() + 1, day.getDate(),
      this.enableTime ? this.selectedHour : undefined, this.enableTime ? this.selectedMinute : 0);
    if (!selected || this.isDayDisabled(selected)) return;
    this.selectedDate = selected;
    this.dispatchEvent(new CustomEvent('date-selected', { detail: this.selectedDate }));
    if (!this.enableTime) {
      this.dispatchEvent(new CustomEvent('close-request'));
    }
  }

  private handleSelectToday(): void {
    const today = new Date();
    if (this.isDayDisabled(today)) return;
    this.viewDate = new Date(today);
    this.selectedHour = today.getHours();
    this.selectedMinute = today.getMinutes();
    this.handleSelectDate(today);
  }

  private handleClear(): void {
    this.dispatchEvent(new CustomEvent('date-cleared'));
    this.dispatchEvent(new CustomEvent('close-request'));
  }

  private handleHourInput = (e: InputEvent): void => {
    const input = e.target as HTMLInputElement;
    let value = parseInt(input.value) || 0;
    if (this.timeFormat === '12h') {
      value = Math.max(1, Math.min(12, value));
      if (this.selectedHour >= 12 && value !== 12) this.selectedHour = value + 12;
      else if (this.selectedHour < 12 && value === 12) this.selectedHour = 0;
      else this.selectedHour = value;
    } else {
      this.selectedHour = Math.max(0, Math.min(23, value));
    }
    this.emitTimeUpdate();
  };

  private handleMinuteInput = (e: InputEvent): void => {
    const input = e.target as HTMLInputElement;
    let value = parseInt(input.value) || 0;
    value = Math.max(0, Math.min(59, value));
    if (this.minuteIncrement > 1) value = Math.min(59, Math.round(value / this.minuteIncrement) * this.minuteIncrement);
    this.selectedMinute = value;
    this.emitTimeUpdate();
  };

  private setAMPM(period: 'am' | 'pm'): void {
    if (period === 'am' && this.selectedHour >= 12) this.selectedHour -= 12;
    else if (period === 'pm' && this.selectedHour < 12) this.selectedHour += 12;
    this.emitTimeUpdate();
  }

  private handleTimezoneChange = (e: Event): void => {
    this.timezone = (e.target as HTMLSelectElement).value;
    this.emitTimeUpdate();
  };

  private emitTimeUpdate(): void {
    if (this.selectedDate) {
      const selected = createCalendarDate(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, this.selectedDate.getDate(), this.selectedHour, this.selectedMinute);
      if (!selected || this.isDayDisabled(selected)) return;
      this.selectedDate = selected;
      this.dispatchEvent(new CustomEvent('date-selected', { detail: this.selectedDate }));
    }
  }

  // Show/hide lifecycle
  public async show(): Promise<void> {
    this.windowLayer = await DeesWindowLayer.createAndShow({ dimmed: false, ownerElement: this.ownerComponent });
    this.windowLayer.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('close-request'));
    });

    this.menuZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(this, this.menuZIndex);
    this.style.zIndex = this.menuZIndex.toString();

    this.windowLayer.parentElement!.appendChild(this);

    await domtools.plugins.smartdelay.delayFor(0);
    this.visible = true;

    window.addEventListener('scroll', this.handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', this.handleScrollOrResize, { passive: true });
  }

  public async hide(): Promise<void> {
    if (this.isDestroying) return;
    this.isDestroying = true;

    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);
    zIndexRegistry.unregister(this);

    if (this.windowLayer) {
      this.windowLayer.destroy();
      this.windowLayer = null;
    }

    this.visible = false;
    await domtools.plugins.smartdelay.delayFor(150);

    if (this.parentElement) this.parentElement.removeChild(this);
    this.isDestroying = false;
  }

  private handleScrollOrResize = (): void => {
    this.dispatchEvent(new CustomEvent('reposition-request'));
  };

  async disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);
    zIndexRegistry.unregister(this);
    await super.disconnectedCallback();
  }
}
